import { Divider, Typo, Button as GddsButton } from '@geberit/gdds';
import { useEffect, useState } from 'react';
import type { Dispatch } from 'redux';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/navigation';

// styles
import styles from './address-container.module.scss';

// components
import { Headline } from 'components/ContentElementsGdds/headline/headline';
import { Button, BUTTON_BACKGROUND, BUTTON_COLORS, BUTTON_TYPES } from 'components/Button/Button';
import { Translation } from 'components/Translation/Translation';
import { Loader } from 'components/Loader/Loader';
import CartTable from 'components/checkout/components/cart-table/cart-table';
import AddressForm from 'components/checkout/components/address-form';
import ErrorMessageBox from 'components/checkout/components/error-message-box/error-message-box';
import LinkWrapper from 'components/checkout/components/link-wrapper/link-wrapper';

// utils
import { isEmpty } from 'utils/is-empty';
import { useXy } from 'utils/hooks/use-xy';
import { getCartAction, fetchUserDataAction } from 'components/checkout/actions';
import { currentLanguageSelector } from 'utils/selectors/languageSelectors';
import { translationKeysSelector } from 'utils/selectors/translationSelectors';
import { hasSessionData, userType } from 'components/checkout/util/helper-functions';
import { ZipCodeValidationData } from 'components/checkout/action.types';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { Formats } from 'components/ContentElementsGdds/headline/headlines.types';
import {
  activeStepSelector,
  cartErrorSelector,
  deliveryAddressSelector,
  deliveryZipCodeValidationDataSelector,
  getCartSelector,
  isCartFetchingSelector,
  isFetchingZipCodeValidationSelector,
  isUserDataFetchingSelector,
  userDataErrorSelector,
  userDataSelector,
  zipCodeValidationDataSelector,
  zipCodeValidationErrorSelector,
  zipCodeValidationFieldSelector,
} from 'components/checkout/selectors';
import {
  webshopBasketPageUrlSelector,
  webshopShippingInfoSelector,
} from 'utils/selectors/globalsSelectors';

type AddressContainerProps = {
  isLogged: boolean;
  ciamId?: string;
  locale: string;
  sessionId?: string;
};

function AddressContainer({
  isLogged,
  ciamId,
  locale,
  sessionId,
}: Readonly<AddressContainerProps>) {
  const dispatch: Dispatch<any> = useDispatch();
  const router = useRouter();
  const activeStep = useSelector(activeStepSelector);
  const cart = useSelector(getCartSelector);
  const userData = useSelector(userDataSelector);
  const isFetchingCart = useSelector(isCartFetchingSelector);
  const isFetchingUserData = useSelector(isUserDataFetchingSelector);
  const userDataError = useSelector(userDataErrorSelector);
  const cartError = useSelector(cartErrorSelector);
  const isFetchingZipCodeValidation = useSelector(isFetchingZipCodeValidationSelector);
  const zipCodeValidationData = useSelector(zipCodeValidationDataSelector);
  const deliveryZipCodeValidationData = useSelector(deliveryZipCodeValidationDataSelector);
  const zipCodeValidationError = useSelector(zipCodeValidationErrorSelector);
  const zipCodeValidationFieldName = useSelector(zipCodeValidationFieldSelector);
  const deliveryAddress = useSelector(deliveryAddressSelector);
  const basketPageUrl = useSelector(webshopBasketPageUrlSelector);
  const showShippingInfo = useSelector(webshopShippingInfoSelector);
  const translations = useSelector(translationKeysSelector);
  const lang = useSelector(currentLanguageSelector);
  const translate = useTranslationFunction();
  const isXy = useXy();

  const [isDeliveryCheckboxSelected, setIsDeliveryCheckboxSelected] = useState(
    deliveryAddress?.deliveryCheckbox || false,
  );

  const checkDisableButton = (validationData: Partial<ZipCodeValidationData> | null): boolean =>
    isEmpty(validationData)
      ? !(isLogged || hasSessionData(userType(isLogged)))
      : !validationData.valid;

  let disableButtonDelivery: boolean =
    isDeliveryCheckboxSelected && checkDisableButton(deliveryZipCodeValidationData);

  useEffect(() => {
    const id = isLogged ? ciamId : sessionId;
    if (isLogged) {
      dispatch(fetchUserDataAction(locale, id!));
    }
    dispatch(getCartAction(locale, id, isLogged));
  }, [ciamId, isLogged, locale, sessionId, dispatch, lang]);

  if (isFetchingCart || isFetchingUserData) {
    return (
      <div className={styles.loaderWrapper}>
        <Loader size="medium" />
      </div>
    );
  }

  const submitAddressForm = () => {
    document.getElementById('addressFormSubmitBtn')?.click();
  };

  return (
    <>
      <div className={styles.bottomMargin}>
        {isXy ? (
          <Headline
            tag={Formats.h1}
            className={styles.bottomMargin}
            title={translate('web20_checkout_address_heading_part1')}
            subtitle={translate('web20_checkout_address_heading_part2')}
          />
        ) : (
          <h1 className={styles.h1}>
            <Translation id="web20_checkout_address_heading_part1" />
            <span>
              <Translation id="web20_checkout_address_heading_part2" />
            </span>
          </h1>
        )}
        {isLogged &&
          userData?.sapCustomerNumber &&
          (isXy ? (
            <>
              <Typo tag="span">{translate('web20_checkout_address_logged_user_label')}</Typo>
              <Typo tag="p" fontWeight={700}>
                {translate('web20_checkout_address_logged_user_customer_nr')}{' '}
                {userData.sapCustomerNumber}
              </Typo>
            </>
          ) : (
            <p>
              <Translation id="web20_checkout_address_logged_user_label" />
              <span className={styles.customerNumberText}>
                <Translation id="web20_checkout_address_logged_user_customer_nr" />{' '}
                {userData.sapCustomerNumber}
              </span>
            </p>
          ))}
        <div className={`grid-x ${styles.bottomMargin}`}>
          <div className="cell small-12  medium-6 large-6">
            {isXy ? (
              <Headline
                className={styles.formHeading}
                tag={Formats.h4}
                variant={Formats.subtitle}
                title={
                  isLogged
                    ? translate('web20_checkout_address_logged_user_invoice_form_heading')
                    : translate('web20_checkout_address_invoice_form_heading')
                }
              />
            ) : (
              <h4 className={styles.formHeading}>
                {isLogged ? (
                  <Translation id="web20_checkout_address_logged_user_invoice_form_heading" />
                ) : (
                  <Translation id="web20_checkout_address_invoice_form_heading" />
                )}
              </h4>
            )}
          </div>
          <div className="cell small-12  medium-6 large-6">
            <div className={styles.formExplanationText}>
              <Translation id="web20_checkout_address_form_explanation_text" />
            </div>
          </div>
        </div>
        <div className="grid-x">
          <AddressForm
            userData={userDataError || isEmpty(userData) ? {} : userData}
            sessionId={sessionId}
            isLogged={isLogged}
            userDataError={userDataError}
            locale={locale}
            ciamId={ciamId}
            zipCodeValidationData={zipCodeValidationData}
            deliveryZipCodeValidationData={deliveryZipCodeValidationData}
            zipCodeValidationError={zipCodeValidationError}
            disableButtonDelivery={disableButtonDelivery}
            isFetchingZipCodeValidation={isFetchingZipCodeValidation}
            zipCodeValidationFieldName={zipCodeValidationFieldName}
            setIsDeliveryCheckboxSelected={setIsDeliveryCheckboxSelected}
          />
        </div>
      </div>
      {isXy ? <Divider /> : <hr className={styles.basketLine} />}
      <div className={styles.topMargin}>
        {isXy ? (
          <Headline
            className={styles.bottomMargin}
            tag={Formats.h3}
            title={translate('web20_checkout_address_basket_table_heading')}
          />
        ) : (
          <h3 className={`${styles.bottomMargin} ${styles.h3}`}>
            <Translation id="web20_checkout_address_basket_table_heading" />
          </h3>
        )}
        {!isEmpty(cart) || !cartError ? (
          <div className="grid-x">
            <div className="cell large-12">
              <CartTable data={cart?.lineItemList ?? []} vatInc />
            </div>
            <div
              className={`cell grid-x grid-padding-x ${styles.bottomMargin} ${styles.cartFooter}`}
            >
              <div
                className={`cell small-12  medium-8 large-8 align-self-middle ${styles.cartFooterText}`}
              >
                <div>
                  <Translation id="web20_checkout_address_basket_table_footer_description" />
                </div>
                {showShippingInfo && (
                  <div>
                    <Translation id="web20_webshop_shipping_info" />
                  </div>
                )}
              </div>
              <div className="cell small-6 medium-2 large-2 align-self-middle">
                <Translation id="web20_checkout_address_basket_table_headings_price" />
              </div>
              <div className="cell small-6 medium-2 large-2 align-self-middle bold">
                {cart?.total} <Translation id="web20_checkout_country_default_currency" />
              </div>
            </div>
          </div>
        ) : (
          <ErrorMessageBox>
            {'700 - '}
            <Translation id="web20_checkout_address_cart_not_available_error" />
            <LinkWrapper translationLink={translations?.web20_checkout_link_customer_service} />
          </ErrorMessageBox>
        )}
        <div className={`${styles.buttonsWrapper} ${styles.bottomMargin}`}>
          {activeStep !== 3 &&
            (isXy ? (
              <GddsButton
                symbol="arrow_left"
                className={styles.editButton}
                onClick={() => router.push(basketPageUrl ?? '')}
              >
                {translations?.web20_checkout_address_button_back}
              </GddsButton>
            ) : (
              <Button
                type={BUTTON_TYPES.DEFAULT}
                symbol="arrow-link-light-left"
                color={BUTTON_COLORS.BLACK}
                background={BUTTON_BACKGROUND.WHITE}
                onClick={() => router.push(basketPageUrl ?? '')}
                className={styles.editButton}
              >
                {translations?.web20_checkout_address_button_back}
              </Button>
            ))}
          {isXy ? (
            <GddsButton
              disabled={
                isFetchingZipCodeValidation ||
                zipCodeValidationError ||
                !zipCodeValidationData?.valid ||
                disableButtonDelivery
              }
              className="form-item--button"
              stylingType="primary"
              symbol="arrow_right"
              onClick={submitAddressForm}
            >
              {translations?.web20_checkout_address_button_next}
            </GddsButton>
          ) : (
            <Button
              disabled={
                isFetchingZipCodeValidation ||
                zipCodeValidationError ||
                !zipCodeValidationData?.valid ||
                disableButtonDelivery
              }
              className="form-item--button"
              type={BUTTON_TYPES.DEFAULT}
              symbol="arrow-link-light-right"
              color={BUTTON_COLORS.WHITE}
              background={BUTTON_BACKGROUND.PRIMARY}
              onClick={submitAddressForm}
            >
              {translations?.web20_checkout_address_button_next}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

export default AddressContainer;
